import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, ArrowRight } from 'lucide-react';
import { useCart } from '../hooks/useCart';
import CartItem from './CartItem';
import {
  getCustomAmount,
  getCustomAmountPlaceholderDeduction,
  getCustomAmountTotal,
  getCustomAmounts,
  isCustomAmountProduct,
  setCustomAmount,
} from '../utils/customAmount';

const Cart: React.FC = () => {
  const { cart, loading, error, clearCart, getCartTotal } = useCart();
  const [customAmounts, setCustomAmountsState] = React.useState<Record<string, string>>(() => getCustomAmounts());

  const customAmountItems = cart?.items.filter(item => isCustomAmountProduct(item.product)) || [];

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const getItemsTotal = () => {
    if (!cart) return 0;
    return getCartTotal() - getCustomAmountPlaceholderDeduction(cart.items);
  };

  const getDisplayTotal = () => {
    if (!cart) return 0;
    return getItemsTotal() + getCustomAmountTotal(cart.items);
  };

  const handleCustomAmountChange = (productId: string, value: string) => {
    setCustomAmountsState(prev => ({ ...prev, [productId]: value }));
    if (value) {
      setCustomAmount(productId, value);
    }
  };

  React.useEffect(() => {
    if (customAmountItems.length > 0) {
      setCustomAmountsState(getCustomAmounts());
    }
  }, [cart?.items.length, customAmountItems.length]);

  if (loading && !cart) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 rounded w-1/4 mb-6" style={{ backgroundColor: '#2a2a2a' }}></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="h-24 rounded" style={{ backgroundColor: '#2a2a2a' }}></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error && !cart) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-2">Error loading cart</div>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <ShoppingCart className="h-16 w-16 mx-auto mb-4" style={{ color: '#39ff14' }} />
          <h2 className="text-2xl font-bold mb-2 tracking-wider" style={{ color: '#e0e0e0', fontFamily: "'911 Porscha', sans-serif", fontStyle: 'italic' }}>YOUR CART IS EMPTY</h2>
          <p className="mb-6" style={{ color: '#bf00ff' }}>Fill your cart up you cuck!</p>
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 rounded-md transition-colors font-bold"
            style={{ backgroundColor: '#39ff14', color: '#0a0a0a' }}
          >
            CONTINUE SHOPPING
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-wider" style={{ color: '#39ff14', fontFamily: "'911 Porscha', sans-serif", fontStyle: 'italic' }}>SHOPPING CART</h1>
        <button
          onClick={clearCart}
          className="text-sm transition-colors font-medium"
          style={{ color: '#ff4444' }}
        >
          CLEAR CART
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="space-y-4">
            {cart.items.map((item) => (
              <CartItem key={item.productId} item={item} />
            ))}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="p-6 rounded-lg border" style={{ backgroundColor: '#1a1a1a', borderColor: '#39ff14' }}>
            <h2 className="text-lg font-semibold mb-4 tracking-wider" style={{ color: '#39ff14', fontFamily: "'911 Porscha', sans-serif", fontStyle: 'italic' }}>ORDER SUMMARY</h2>

            {customAmountItems.map((item) => (
              <div key={item.productId} className="mb-6 p-4 rounded-lg border-2" style={{ backgroundColor: '#0f0f0f', borderColor: '#39ff14' }}>
                <label htmlFor={`customAmount-${item.productId}`} className="block text-sm font-medium mb-2" style={{ color: '#e0e0e0' }}>
                  {`Enter ${item.product?.name} Amount *`}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 font-bold" style={{ color: '#39ff14' }}>$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    id={`customAmount-${item.productId}`}
                    value={customAmounts[item.productId] ?? getCustomAmount(item.productId)}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                        handleCustomAmountChange(item.productId, value);
                      }
                    }}
                    className="w-full pl-8 pr-3 py-2 border rounded-md focus:ring-2 focus:outline-none"
                    style={{
                      color: '#e0e0e0',
                      backgroundColor: '#1a1a1a',
                      borderColor: '#2a2a2a'
                    }}
                    placeholder="0.00"
                  />
                </div>
                <p className="text-xs mt-2" style={{ color: '#a0a0a0' }}>
                  Enter any amount you'd like (minimum $1.00)
                </p>
              </div>
            ))}

            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span style={{ color: '#a0a0a0' }}>Items ({cart.items.length})</span>
                <span style={{ color: '#e0e0e0' }}>{formatPrice(getItemsTotal())}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: '#a0a0a0' }}>Shipping</span>
                <span style={{ color: '#e0e0e0' }}>Free</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: '#a0a0a0' }}>Tax</span>
                <span style={{ color: '#e0e0e0' }}>$0.00</span>
              </div>
              {customAmountItems.map((item) => (
                <div key={`summary-${item.productId}`} className="flex justify-between text-sm">
                  <span style={{ color: '#a0a0a0' }}>{item.product?.name}</span>
                  <span style={{ color: '#e0e0e0' }}>
                    ${customAmounts[item.productId] ?? getCustomAmount(item.productId) ?? '0.00'}
                  </span>
                </div>
              ))}
              <div className="pt-3" style={{ borderTop: '1px solid #2a2a2a' }}>
                <div className="flex justify-between text-lg font-semibold">
                  <span style={{ color: '#39ff14' }}>TOTAL</span>
                  <span style={{ color: '#39ff14' }}>{formatPrice(getDisplayTotal())}</span>
                </div>
              </div>
            </div>

            <Link
              to="/checkout"
              className="w-full flex items-center justify-center px-4 py-2 rounded-md transition-colors font-bold"
              style={{ backgroundColor: '#39ff14', color: '#0a0a0a' }}
            >
              PROCEED TO CHECKOUT
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
